import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reportsService, usersService, getErrorMessage } from '../services/api';
import { toast } from 'sonner';

// Native Button Component
const NativeButton = ({ children, onClick, type = 'button', variant = 'primary', disabled, className = '', style = {}, ...props }) => {
  const baseStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 'none',
    transition: 'all 0.2s',
    opacity: disabled ? 0.6 : 1
  };

  const variants = {
    primary: { backgroundColor: '#2563eb', color: 'white' },
    secondary: { backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
    outline: { backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' },
    email: { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
    ghost: { backgroundColor: 'transparent', color: '#374151' }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...baseStyle, ...variants[variant], ...style }}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
};

// Native Card Component
const NativeCard = ({ children, className = '', style = {} }) => (
  <div
    style={{
      backgroundColor: 'var(--card-bg)',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      border: '1px solid var(--card-border)',
      ...style
    }}
    className={className}
  >
    {children}
  </div>
);

// Native Input Component
const NativeInput = ({ label, id, type = 'text', value, onChange, placeholder, className = '', icon, ...props }) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <label htmlFor={id} style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
        {label}
      </label>
    )}
    <div style={{ position: 'relative' }}>
      {icon && (
        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>
          {icon}
        </span>
      )}
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: icon ? '8px 12px 8px 36px' : '8px 12px',
          borderRadius: '6px',
          border: '1px solid #d1d5db',
          fontSize: '14px',
          outline: 'none',
          boxSizing: 'border-box'
        }}
        className={className}
        {...props}
      />
    </div>
  </div>
);

// Native Select Component
const NativeSelect = ({ label, id, value, onChange, options, className = '', ...props }) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <label htmlFor={id} style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
        {label}
      </label>
    )}
    <select
      id={id}
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid var(--card-border)',
        fontSize: '14px',
        outline: 'none',
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
        boxSizing: 'border-box',
        cursor: 'pointer'
      }}
      className={className}
      {...props}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// Native Badge Component
const NativeBadge = ({ children, variant = 'default' }) => {
  const variants = {
    default: { backgroundColor: '#2563eb', color: 'white' },
    secondary: { backgroundColor: '#f3f4f6', color: '#6b7280' },
    success: { backgroundColor: '#dcfce7', color: '#166534' },
    warning: { backgroundColor: '#fef3c7', color: '#92400e' }
  };

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500',
      ...variants[variant]
    }}>
      {children}
    </span>
  );
};

// Native Modal Component
const NativeModal = ({ isOpen, onClose, title, children, disabled }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !disabled) {
          onClose();
        }
      }}
      data-testid="modal-backdrop"
    >
      <div
        style={{
          backgroundColor: 'var(--card-bg)',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: '450px',
          overflow: 'hidden',
          border: '1px solid var(--card-border)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--card-border)',
            background: 'linear-gradient(to right, #2563eb, #1d4ed8)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'white', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            style={{
              padding: '4px',
              borderRadius: '4px',
              border: 'none',
              background: 'transparent',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              color: 'white'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Icon Components (inline SVG)
const LoaderIcon = ({ className = '' }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </svg>
);

const PrinterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const FileTextIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ZapIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const WalletIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

export default function ReportsPage() {
  const { user, isAdmin, getEffectiveRole, getEffectiveSiteId, isHesapYoneticisi, isSimulationActive, simulatedRole } = useAuth();
  const [statement, setStatement] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Efektif rol kontrolü - simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveAdmin = ['super_admin', 'admin'].includes(effectiveRole);
  
  // Simülasyon aktifken gösterilecek kullanıcı adı
  const getDisplayUserName = () => {
    if (isSimulationActive && isSimulationActive()) {
      const roleNames = {
        'hesap_yoneticisi': 'Hesap Yöneticisi',
        'site_manager': 'Hesap Yöneticisi',
        'admin': 'Admin',
        'super_admin': 'Super Admin'
      };
      return `${roleNames[effectiveRole] || effectiveRole} (Simülasyon)`;
    }
    return statement?.user_name || statement?.user_email || user?.full_name || '-';
  };
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  
  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // View mode: 'my' for current user, 'user' for specific user (admin only), 'all' for summary
  // Default to 'my' for better UX - buttons are active immediately
  const [viewMode, setViewMode] = useState('my');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch users list for admin (veya Hesap Yöneticisi - kendi sitesi)
      if (isEffectiveAdmin || isAdmin() || isEffectiveHesapYoneticisi || isHesapYoneticisi()) {
        try {
          let usersData = await usersService.list();
          usersData = Array.isArray(usersData) ? usersData : [];
          // Hesap Yöneticisi için sadece kendi sitesinin kullanıcıları
          if ((isEffectiveHesapYoneticisi || isHesapYoneticisi()) && !isEffectiveAdmin && !isAdmin() && effectiveSiteId) {
            usersData = usersData.filter(u => u.site_id === effectiveSiteId);
          }
          setUsers(usersData);
        } catch (userErr) {
          console.warn('Kullanıcı listesi yüklenemedi:', userErr);
          setUsers([]);
        }
      }

      // Fetch report based on view mode
      let reportData = null;
      const startIso = startDate ? new Date(startDate).toISOString() : null;
      const endIso = endDate ? new Date(endDate + 'T23:59:59').toISOString() : null;

      if (viewMode === 'my') {
        try {
          reportData = await reportsService.myStatement(startIso, endIso);
          // Null/undefined kontrolü - boş obje ile değiştir
          if (!reportData) {
            reportData = { sessions: [], total_sessions: 0, total_energy: 0, total_cost: 0 };
          }
          setStatement(reportData);
        } catch (reportErr) {
          console.warn('Rapor yüklenemedi:', reportErr);
          setStatement({ sessions: [], total_sessions: 0, total_energy: 0, total_cost: 0 });
        }
      } else if (viewMode === 'user' && selectedUserId) {
        try {
          reportData = await reportsService.user(selectedUserId, startIso, endIso);
          if (!reportData) {
            reportData = { sessions: [], total_sessions: 0, total_energy: 0, total_cost: 0 };
          }
          setStatement(reportData);
        } catch (reportErr) {
          console.warn('Kullanıcı raporu yüklenemedi:', reportErr);
          setStatement({ sessions: [], total_sessions: 0, total_energy: 0, total_cost: 0 });
        }
      } else if (viewMode === 'all' && (isEffectiveAdmin || isAdmin())) {
        try {
          reportData = await reportsService.all();
          // Array kontrolü
          let summaryList = Array.isArray(reportData) ? reportData : [];
          // Hesap Yöneticisi için sadece kendi sitesinin raporları (all modunda gösterilmez aslında)
          if ((isEffectiveHesapYoneticisi || isHesapYoneticisi()) && !isEffectiveAdmin && effectiveSiteId) {
            summaryList = summaryList.filter(r => r.site_id === effectiveSiteId);
          }
          setStatement({ summaryList });
        } catch (reportErr) {
          console.warn('Tüm raporlar yüklenemedi:', reportErr);
          setStatement({ summaryList: [] });
        }
      } else if (viewMode === 'user' && !selectedUserId) {
        setStatement(null);
      }
    } catch (err) {
      console.error('Veri çekme hatası:', err);
      setError(getErrorMessage(err));
      // Hata durumunda bile boş state set et - crash önleme
      setStatement(null);
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedUserId, startDate, endDate, isAdmin, isEffectiveAdmin, isEffectiveHesapYoneticisi, isHesapYoneticisi, effectiveSiteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const startIso = startDate ? new Date(startDate).toISOString() : null;
      const endIso = endDate ? new Date(endDate + 'T23:59:59').toISOString() : null;

      let blob;
      if (viewMode === 'my') {
        blob = await reportsService.downloadMyPdf(startIso, endIso);
      } else if (viewMode === 'user' && selectedUserId) {
        blob = await reportsService.downloadPdf(selectedUserId, startIso, endIso);
      } else {
        toast.error('PDF indirmek için bir kullanıcı seçin');
        setPdfLoading(false);
        return;
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `ekstre_${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF başarıyla indirildi');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!statement || (viewMode !== 'my' && viewMode !== 'user')) {
      toast.error('Dışa aktarılacak veri yok');
      return;
    }

    const sessions = statement.sessions || [];
    if (sessions.length === 0) {
      toast.error('Dışa aktarılacak oturum yok');
      return;
    }

    // Create CSV content
    const headers = ['Tarih', 'İstasyon', 'Enerji (kWh)', 'Tutar (TL)', 'Durum'];
    const rows = sessions.map(s => [
      s.start_time ? new Date(s.start_time).toLocaleString('tr-TR') : '-',
      s.station_name || s.station_id || '-',
      (s.energy_consumed || 0).toFixed(2),
      (s.cost || 0).toFixed(2),
      s.is_active ? 'Devam Ediyor' : 'Tamamlandı'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ekstre_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('CSV başarıyla indirildi');
  };

  const handleOpenEmailModal = () => {
    // Pre-fill email if viewing a specific user's report
    if (viewMode === 'user' && selectedUserId && statement) {
      const selectedUser = users.find(u => u.id === selectedUserId);
      setEmailAddress(selectedUser?.email || '');
    } else if (viewMode === 'my' && user) {
      setEmailAddress(user.email || '');
    } else {
      setEmailAddress('');
    }
    setShowEmailModal(true);
  };

  const handleCloseEmailModal = () => {
    if (emailLoading) return;
    setShowEmailModal(false);
    setEmailAddress('');
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error('Geçerli bir email adresi girin');
      return;
    }

    setEmailLoading(true);
    try {
      const startIso = startDate ? new Date(startDate).toISOString() : null;
      const endIso = endDate ? new Date(endDate + 'T23:59:59').toISOString() : null;

      const requestData = {
        to_email: emailAddress,
        start_date: startIso,
        end_date: endIso
      };

      // If admin viewing another user's report, include user_id
      if (viewMode === 'user' && selectedUserId) {
        requestData.user_id = selectedUserId;
      }

      await reportsService.sendEmail(requestData);
      toast.success(`Ekstre emaili gönderildi: ${emailAddress}`);
      setShowEmailModal(false);
      setEmailAddress('');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setEmailLoading(false);
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '-';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} dk`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours} sa ${mins} dk`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #2563eb', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircleIcon />
          {error}
        </span>
        <NativeButton variant="outline" onClick={fetchData}>
          <RefreshIcon />
          Tekrar Dene
        </NativeButton>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }} data-testid="reports-page">
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: 'var(--text-primary)' }}>
              {isAdmin() ? 'Raporlar & Ekstreler' : 'Şarj Ekstrem'}
            </h1>
            <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              {isAdmin() 
                ? 'Kullanıcı şarj geçmişi raporlarını görüntüleyin ve PDF olarak indirin' 
                : 'Şarj geçmişinizi görüntüleyin ve PDF olarak indirin'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <NativeButton 
              variant="outline" 
              onClick={handlePrint} 
              data-testid="print-report-button"
            >
              <PrinterIcon />
              Yazdır
            </NativeButton>
            <div 
              title={viewMode === 'all' ? "Dışa aktarmak için 'Kendi Ekstrem' veya 'Kullanıcı Detay' görünümüne geçin" : undefined}
              style={{ display: 'inline-block' }}
            >
              <NativeButton 
                variant="outline" 
                onClick={handleExportCsv}
                disabled={viewMode === 'all'}
                data-testid="export-csv-button"
              >
                <DownloadIcon />
                CSV
              </NativeButton>
            </div>
            <div 
              title={viewMode === 'all' ? "PDF indirmek için 'Kendi Ekstrem' veya 'Kullanıcı Detay' görünümüne geçin" : undefined}
              style={{ display: 'inline-block' }}
            >
              <NativeButton 
                variant="primary"
                onClick={handleDownloadPdf}
                disabled={pdfLoading || viewMode === 'all'}
                data-testid="download-pdf-button"
              >
                {pdfLoading ? (
                  <LoaderIcon />
                ) : (
                  <FileTextIcon />
                )}
                PDF İndir
              </NativeButton>
            </div>
            <div 
              title={viewMode === 'all' ? "Email göndermek için 'Kendi Ekstrem' veya 'Kullanıcı Detay' görünümüne geçin" : undefined}
              style={{ display: 'inline-block' }}
            >
              <NativeButton 
                variant="email"
                onClick={handleOpenEmailModal}
                disabled={viewMode === 'all'}
                data-testid="send-email-button"
              >
                <MailIcon />
                Email Gönder
              </NativeButton>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <NativeCard style={{ marginBottom: '24px' }}>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', alignItems: 'end' }}>
            {/* View Mode (Admin Only) */}
            {isAdmin() && (
              <NativeSelect
                label="Görünüm"
                id="view-mode"
                value={viewMode}
                onChange={(e) => {
                  setViewMode(e.target.value);
                  if (e.target.value !== 'user') setSelectedUserId('');
                }}
                options={[
                  { value: 'all', label: 'Tüm Kullanıcılar (Özet)' },
                  { value: 'user', label: 'Kullanıcı Detay' },
                  { value: 'my', label: 'Kendi Ekstrem' }
                ]}
                data-testid="view-mode-select"
              />
            )}

            {/* User Selection (Admin Only) */}
            {isAdmin() && viewMode === 'user' && (
              <NativeSelect
                label="Kullanıcı"
                id="user-select"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                options={[
                  { value: '', label: 'Kullanıcı seçin' },
                  ...users.map((u) => ({ value: u.id, label: u.full_name || u.email }))
                ]}
                data-testid="user-select"
              />
            )}

            {/* Date Range */}
            {(viewMode === 'my' || viewMode === 'user') && (
              <>
                <NativeInput
                  label="Başlangıç Tarihi"
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  icon={<CalendarIcon />}
                  data-testid="start-date-input"
                />
                <NativeInput
                  label="Bitiş Tarihi"
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  icon={<CalendarIcon />}
                  data-testid="end-date-input"
                />
              </>
            )}

            {/* Refresh Button */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <NativeButton onClick={fetchData} variant="outline" style={{ width: '100%' }}>
                <RefreshIcon />
                Yenile
              </NativeButton>
            </div>
          </div>
        </div>
      </NativeCard>

      {/* Summary Cards - for user detail or my statement */}
      {statement && (viewMode === 'my' || viewMode === 'user') && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <NativeCard>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ padding: '12px', backgroundColor: '#dbeafe', borderRadius: '8px' }}>
                  <ClockIcon style={{ color: '#2563eb' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>Toplam Oturum</p>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text-primary)', margin: 0 }}>
                    {statement.total_sessions || 0}
                  </p>
                </div>
              </div>
            </div>
          </NativeCard>
          <NativeCard>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ padding: '12px', backgroundColor: '#dcfce7', borderRadius: '8px' }}>
                  <ZapIcon style={{ color: '#16a34a' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>Toplam Enerji</p>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a', margin: 0 }}>
                    {(statement.total_energy || 0).toFixed(2)} kWh
                  </p>
                </div>
              </div>
            </div>
          </NativeCard>
          <NativeCard>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ padding: '12px', backgroundColor: '#ffedd5', borderRadius: '8px' }}>
                  <WalletIcon style={{ color: '#ea580c' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>Toplam Tutar</p>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#ea580c', margin: 0 }}>
                    {(statement.total_cost || 0).toFixed(2)} TL
                  </p>
                </div>
              </div>
            </div>
          </NativeCard>
          <NativeCard>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ padding: '12px', backgroundColor: '#f3e8ff', borderRadius: '8px' }}>
                  <UserIcon style={{ color: '#9333ea' }} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>Kullanıcı</p>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', margin: 0, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getDisplayUserName()}
                  </p>
                </div>
              </div>
            </div>
          </NativeCard>
        </div>
      )}

      {/* Sessions Detail Table - for user detail or my statement */}
      {statement && (viewMode === 'my' || viewMode === 'user') && (
        <NativeCard style={{ marginBottom: '24px' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileTextIcon />
              Şarj Oturumları Detayı
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0' }}>
              {startDate || endDate 
                ? `${startDate || '...'} - ${endDate || '...'} tarihleri arası`
                : 'Tüm zamanlar'}
            </p>
          </div>
          <div style={{ padding: '0' }}>
            {!statement.sessions || statement.sessions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                <FileTextIcon style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: '#d1d5db' }} />
                <p>Bu dönemde şarj oturumu bulunmuyor</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Tarih</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>İstasyon</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>RFID Kart</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Süre</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Enerji</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Birim Fiyat</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Tutar</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.sessions.map((session, index) => (
                      <tr key={session.id || index} style={{ borderBottom: '1px solid #e5e7eb' }} data-testid={`session-row-${index}`}>
                        <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                          {session.start_time 
                            ? new Date(session.start_time).toLocaleString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500' }}>
                          {session.station_name || session.station_id || '-'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                          <span style={{ 
                            backgroundColor: '#f0f9ff', 
                            color: '#0369a1', 
                            padding: '2px 8px', 
                            borderRadius: '4px', 
                            fontSize: '12px',
                            fontFamily: 'monospace'
                          }}>
                            {session.rfid_tag || session.id_tag || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right' }}>
                          {formatDuration(session.start_time, session.end_time)}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right' }}>
                          {(session.energy_consumed || 0).toFixed(2)} kWh
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right', color: '#6b7280' }}>
                          {session.price_per_kwh ? `${session.price_per_kwh.toFixed(2)} TL/kWh` : '-'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                          {(session.cost || 0).toFixed(2)} TL
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <NativeBadge variant={session.is_active ? 'default' : 'secondary'}>
                            {session.is_active ? 'Devam Ediyor' : 'Tamamlandı'}
                          </NativeBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </NativeCard>
      )}

      {/* Summary Table - for all users (admin only) */}
      {statement && viewMode === 'all' && statement.summaryList && (
        <NativeCard style={{ marginBottom: '24px' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileTextIcon />
              Tüm Kullanıcılar Özeti
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0' }}>
              Toplam {statement.summaryList.length} kullanıcı
            </p>
          </div>
          <div style={{ padding: '0' }}>
            {statement.summaryList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                Kullanıcı bulunamadı
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Kullanıcı</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>E-posta</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Oturum Sayısı</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Toplam Enerji</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Toplam Tutar</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.summaryList.map((report, index) => (
                      <tr key={report.user_id || index} style={{ borderBottom: '1px solid #e5e7eb' }} data-testid={`summary-row-${index}`}>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500' }}>
                          {report.user_name || '-'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px' }}>{report.email || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right' }}>{report.total_sessions || 0}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right' }}>
                          {(report.total_energy || 0).toFixed(2)} kWh
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                          {(report.total_cost || 0).toFixed(2)} TL
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <NativeButton
                            variant="ghost"
                            onClick={() => {
                              setSelectedUserId(report.user_id);
                              setViewMode('user');
                            }}
                            data-testid={`view-user-${report.user_id}`}
                          >
                            Detay
                          </NativeButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </NativeCard>
      )}

      {/* No data message */}
      {viewMode === 'user' && !selectedUserId && (
        <NativeCard>
          <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
            <UserIcon style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: '#d1d5db' }} />
            <p>Lütfen bir kullanıcı seçin</p>
          </div>
        </NativeCard>
      )}

      {/* Print-only styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [data-testid="reports-page"], [data-testid="reports-page"] * {
            visibility: visible;
          }
          [data-testid="reports-page"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          button, select, input {
            display: none !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* Email Modal */}
      <NativeModal
        isOpen={showEmailModal}
        onClose={handleCloseEmailModal}
        title={<><MailIcon /> Ekstre Email Gönder</>}
        disabled={emailLoading}
      >
        <form onSubmit={handleSendEmail}>
          <NativeInput
            label="Email Adresi *"
            id="email-address"
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder="ornek@email.com"
            required
            autoFocus
            data-testid="email-input"
          />
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '-8px', marginBottom: '16px' }}>
            Ekstre PDF olarak bu adrese gönderilecektir.
          </p>

          {/* Date range info */}
          <div style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#6b7280', fontSize: '13px' }}>Dönem:</span>
              <span style={{ fontWeight: '500', fontSize: '13px' }}>
                {startDate && endDate 
                  ? `${startDate} - ${endDate}`
                  : 'Tüm zamanlar'}
              </span>
            </div>
            {statement && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px' }}>Toplam Oturum:</span>
                  <span style={{ fontWeight: '500', fontSize: '13px' }}>{statement.total_sessions || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px' }}>Toplam Tutar:</span>
                  <span style={{ fontWeight: '600', fontSize: '13px', color: '#16a34a' }}>
                    {(statement.total_cost || 0).toFixed(2)} TL
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <NativeButton
              type="button"
              variant="outline"
              onClick={handleCloseEmailModal}
              disabled={emailLoading}
            >
              İptal
            </NativeButton>
            <NativeButton
              type="submit"
              variant="primary"
              disabled={emailLoading || !emailAddress}
              data-testid="send-email-submit"
            >
              {emailLoading ? (
                <>
                  <LoaderIcon />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <SendIcon />
                  Gönder
                </>
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>
    </div>
  );
}
